import { useEffect } from "react";
import { useLocation, matchPath } from "react-router-dom";

/**
 * PageMeta — mount once at App-level, sibling to <Canonical />.
 * On every route change it injects/updates:
 *   - <meta name="description">
 *   - <meta name="keywords">
 *   - <meta property="og:title">
 *   - <meta property="og:description">
 *   - <meta property="og:image">
 *   - <meta property="og:type">
 *   - <meta name="twitter:title">
 *   - <meta name="twitter:description">
 *   - <meta name="twitter:image">
 *   - <meta name="robots">
 *
 * Title is left to `usePageTitle` on each page; canonical + og:url are
 * handled by <Canonical />. This keeps concerns separate and avoids
 * pulling react-helmet into every page.
 */

const BRAND = "Get Virtual Growth";
const OG_IMAGE = "https://getvirtualgrowth.com/og-cover.png";

// title suffix ("| Get Virtual Growth") is added by usePageTitle at render.
// The title here is used only for og:title / twitter:title fallback.
const META = {
  "/": {
    title:
      "Automated Outbound Video Engine for founder-led firms | Get Virtual Growth",
    description:
      "Personalised video customer acquisition, fully managed. Get Virtual Growth writes, records, delivers and follows up on named-prospect videos so founders spend time on calls, not on outreach.",
    keywords: [
      "personalised video outreach",
      "outbound video engine",
      "founder-led sales",
      "customer acquisition system",
      "Get Virtual Growth",
    ],
  },
  "/engine": {
    title: "The Engine | Get Virtual Growth",
    description:
      "The Get Virtual Growth Engine: named-prospect video outreach that books meetings on autopilot for coaches, consultants and recruiters. Fully managed. Reply-first.",
    keywords: [
      "outbound video engine",
      "personalised video outreach",
      "meeting booking system",
    ],
  },
  "/build": {
    title: "Build your Video Engine | Get Virtual Growth",
    description:
      "Build a reply-first outbound video engine for your practice in weeks, not quarters. See the mechanism, the cadence and the numbers Get Virtual Growth ships against.",
    keywords: [
      "build outbound engine",
      "video prospecting playbook",
      "founder growth system",
    ],
  },
  "/story": {
    title: "The Quiet Advantage - a short book for founders | Get Virtual Growth",
    description:
      "A four-minute book from Krishna S, founder of Get Virtual Growth. Read slowly, one page at a time. On why the quiet businesses are winning again.",
    keywords: ["founder book", "the quiet advantage", "Krishna S", "founder letter"],
  },
  "/diagnose": {
    title: "Diagnose your growth | Get Virtual Growth",
    description:
      "Choose a free six-question growth diagnosis or a paid eight-page written diagnosis. See exactly where your business is leaking growth. Answers in 48 hours.",
    keywords: [
      "business growth diagnosis",
      "growth audit",
      "founder diagnosis",
      "sales pipeline diagnosis",
    ],
  },
  "/diagnosis": {
    title: "Free growth diagnosis | Get Virtual Growth",
    description:
      "Six honest questions. Instant growth-health score. A written response from Krishna within 48 hours. No credit card, no upsell, no sales script.",
    keywords: ["free growth diagnosis", "growth score", "60 second diagnosis"],
  },
  "/diagnosis-pro": {
    title: "Diagnosis Pro - an eight-page written diagnosis | Get Virtual Growth",
    description:
      "Eleven deeper questions. Krishna researches your business and writes you an eight-page report in plain prose, delivered within 48 hours. One-time $499.",
    keywords: [
      "paid growth diagnosis",
      "eight page business diagnosis",
      "consulting diagnosis",
    ],
  },
  "/diagnosis-pro/success": {
    title: "Diagnosis Pro received | Get Virtual Growth",
    description:
      "Payment confirmed. Krishna will begin work and deliver your written diagnosis within 48 hours.",
    keywords: [],
    noindex: true,
  },
  "/diagnosis-pro/cancel": {
    title: "Diagnosis Pro payment cancelled | Get Virtual Growth",
    description:
      "Payment cancelled. You have not been charged. You can restart the Diagnosis Pro checkout whenever you are ready.",
    keywords: [],
    noindex: true,
  },
  "/strategy-brief": {
    title: "Strategy Brief - private founder questionnaire | Get Virtual Growth",
    description:
      "A private questionnaire before your strategy call with Krishna. Ten quiet minutes so the call goes straight to the work that matters.",
    keywords: [
      "strategy brief",
      "founder questionnaire",
      "pre-call brief",
    ],
    noindex: true,
  },
  "/the-difference": {
    title: "The Difference - twin businesses, one changed | Get Virtual Growth",
    description:
      "Two identical firms, one small decision, two very different outcomes. The Difference is a side-by-side positioning page from Get Virtual Growth.",
    keywords: [
      "before and after",
      "founder growth story",
      "twin case study",
    ],
  },
  "/growth-partnership": {
    title:
      "Growth Partnership - personalised video customer acquisition | Get Virtual Growth",
    description:
      "Fully managed personalised video customer acquisition for founder-led firms across the UAE, Saudi Arabia, Qatar, Bahrain and Kuwait. Book a private strategy call with the founder.",
    keywords: [
      "growth partnership",
      "personalised video outreach",
      "GCC founder-led sales",
      "customer acquisition retainer",
    ],
  },
  "/field-notes": {
    title: "Field Notes - Sunday letters from Krishna | Get Virtual Growth",
    description:
      "Short, honest stories from inside real businesses. A two-minute read every Sunday, or a two-minute listen in Krishna's voice.",
    keywords: [
      "field notes",
      "founder letters",
      "small business stories",
      "Sunday letter",
    ],
  },
  "/privacy": {
    title: "Privacy Policy | Get Virtual Growth",
    description:
      "How Get Virtual Growth collects, uses and protects the information founders share when working with us.",
    keywords: [],
    noindex: false,
  },
  "/terms": {
    title: "Terms of Use | Get Virtual Growth",
    description:
      "The terms of use that apply when you use the Get Virtual Growth website and services.",
    keywords: [],
    noindex: false,
  },
};

// Field Notes detail pages get a per-slug fallback so the note title
// and subtitle become the OG title/description as soon as the note API
// resolves. Until then we ship a safe generic.
const FIELD_NOTE_FALLBACK = {
  title: "Field Note - a Sunday letter from Krishna | Get Virtual Growth",
  description:
    "A short, honest story from inside real businesses. A two-minute read from Krishna S, founder of Get Virtual Growth.",
  keywords: ["field note", "founder letter", "Sunday letter"],
};

function upsert(selector, tag, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tag);
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function resolveMeta(pathname) {
  if (META[pathname]) return META[pathname];
  // Detail routes (field notes) — safe generic until the note resolves.
  if (matchPath("/field-notes/:slug", pathname)) return FIELD_NOTE_FALLBACK;
  // Fallback for any unmapped path.
  return META["/"];
}

export default function PageMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const m = resolveMeta(pathname);
    const desc = m.description || "";
    const title = m.title || BRAND;
    const kws = (m.keywords || []).join(", ");
    const img = m.ogImage || OG_IMAGE;

    // Own the browser tab title too so every route has a unique, canonical
    // <title>. Page components can still override this after data loads
    // (e.g. FieldNote sets its note title via usePageTitle). Their effect
    // runs after PageMeta's on that route change and cleanly wins.
    document.title = title;

    upsert('meta[name="description"]', "meta", { name: "description", content: desc });
    if (kws) upsert('meta[name="keywords"]', "meta", { name: "keywords", content: kws });

    upsert('meta[property="og:title"]', "meta", { property: "og:title", content: title });
    upsert('meta[property="og:description"]', "meta", { property: "og:description", content: desc });
    upsert('meta[property="og:image"]', "meta", { property: "og:image", content: img });
    upsert('meta[property="og:type"]', "meta", { property: "og:type", content: "website" });
    upsert('meta[property="og:site_name"]', "meta", { property: "og:site_name", content: BRAND });

    upsert('meta[name="twitter:card"]', "meta", { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', "meta", { name: "twitter:title", content: title });
    upsert('meta[name="twitter:description"]', "meta", { name: "twitter:description", content: desc });
    upsert('meta[name="twitter:image"]', "meta", { name: "twitter:image", content: img });

    upsert('meta[name="robots"]', "meta", {
      name: "robots",
      content: m.noindex
        ? "noindex, nofollow"
        : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
    });
  }, [pathname]);

  return null;
}
